import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { createCheckoutSession } from '../api';

const PLANS = [
  {
    id: 'individual',
    name: 'Individual',
    price: '$9.99',
    period: '/month',
    description: 'For a single practitioner',
    features: [
      'Full facility directory with all filters',
      'Complete address, phone, and bed data',
      'Co-occurring and bed-type breakdowns',
      'Personal favorites list',
      'Add notes to facilities',
      'Community insurance info',
      '1 active session at a time',
    ],
    cta: 'Start free month',
    highlight: false,
  },
  {
    id: 'org',
    name: 'Organization',
    price: '$25',
    period: '/month',
    description: 'For teams and agencies',
    features: [
      'Everything in Individual',
      'Unlimited simultaneous staff sessions',
      'Shared org-wide favorites list',
      'Shared org-wide facility notes',
      'All staff see same saved resources',
      'Best value for teams of 3+',
    ],
    cta: 'Start free month',
    highlight: true,
  },
];

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function Subscribe() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null); // 'individual' | 'org' | null
  const [error, setError] = useState('');

  const handleSelect = async (planId) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    setError('');
    setLoadingPlan(planId);
    try {
      const { url } = await createCheckoutSession({ plan: planId });
      // Redirect to Stripe hosted checkout
      window.location.href = url;
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        'Could not start checkout. Please try again.'
      );
      setLoadingPlan(null);
    }
  };

  const alreadySubscribed =
    user?.account_type === 'subscriber_individual' ||
    user?.account_type === 'subscriber_org' ||
    user?.account_type === 'comp_individual' ||
    user?.account_type === 'comp_org' ||
    user?.account_type === 'admin';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>Subscribe | WA BHA Treatment Finder</title>
        <meta name="description" content="Subscribe to WA BHA Treatment Finder for full access to Washington State's behavioral health facility directory. Plans available for individuals and organizations." />
      </Helmet>
      <Header right={isAuthenticated ? undefined : null} />

      <main className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full">
        <div className="text-center mb-10">
          <div className="inline-block bg-green-100 text-green-800 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            First month free — no charge today
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Full access for your whole team
          </h1>
          <p className="text-gray-500 max-w-lg mx-auto">
            Unlock complete facility details, advanced filters, bed-type breakdowns,
            favorites, and community notes for every WA behavioral health facility.
          </p>
        </div>

        {alreadySubscribed && (
          <div className="mb-8 bg-green-50 border border-green-200 rounded-xl px-5 py-4 text-center">
            <p className="text-sm font-medium text-green-800">
              You already have full access.{' '}
              <Link to="/" className="underline">Return to search</Link> or{' '}
              <Link to="/account" className="underline">manage your account</Link>.
            </p>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-2xl border-2 p-7 flex flex-col ${
                plan.highlight
                  ? 'border-blue-500 shadow-md'
                  : 'border-gray-200 shadow-sm'
              }`}
            >
              {plan.highlight && (
                <span className="self-start bg-blue-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full mb-4">
                  Best for teams
                </span>
              )}

              <h2 className="text-xl font-bold text-gray-900">{plan.name}</h2>
              <p className="text-sm text-gray-500 mt-1 mb-5">{plan.description}</p>

              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                <span className="text-gray-500 text-sm">{plan.period}</span>
              </div>

              <ul className="space-y-2.5 mb-8 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckIcon />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelect(plan.id)}
                disabled={!!loadingPlan || alreadySubscribed}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors ${
                  alreadySubscribed
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : plan.highlight
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                }`}
              >
                {loadingPlan === plan.id ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Redirecting to checkout…
                  </>
                ) : plan.cta}
              </button>
            </div>
          ))}
        </div>

        {error && (
          <p className="text-center text-sm text-red-600 mt-6">{error}</p>
        )}

        {/* Agency / Org perks callout */}
        <div className="max-w-2xl mx-auto mt-8 bg-blue-50 border border-blue-200 rounded-2xl px-6 py-5">
          <h3 className="text-sm font-bold text-blue-900 mb-3">
            Agency account? Here's what the Organization plan includes for your facility listing:
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
            {[
              'Claim and link your facility listing',
              'Edit and maintain your own profile',
              'Add point of contact info visible to case managers',
              'Self-report real-time bed availability',
              '"Accepting referrals" green/red toggle',
              'Add program description and specialties',
              'Languages spoken & accessibility info',
              'Insurance accepted & telehealth flag',
              'Referral requirements for your facility',
              'Diagnoses and conditions you accept',
            ].map((perk) => (
              <div key={perk} className="flex items-start gap-2 text-sm text-blue-800">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                {perk}
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">
          First month free. Then billed monthly. Cancel anytime from your account page.
          Payments processed securely by Stripe.
        </p>

        <p className="text-center text-xs text-gray-400 mt-2">
          By subscribing you agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
            Terms of Service and No Refund Policy
          </a>.
        </p>

        {!isAuthenticated && (
          <p className="text-center text-sm text-gray-500 mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 font-medium hover:underline">Sign in</Link>
          </p>
        )}

        <div className="text-center mt-4">
          <Link to="/" className="text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2">
            Continue with limited guest access
          </Link>
        </div>
      </main>
    </div>
  );
}
